import { useCallback, useEffect, useRef } from "react";

import { useMedplum } from "@medplum/react";

import { useForm, zodResolver } from "@mantine/form";

import { isValid } from "date-fns";

import { useTimerContext } from "@/components/Timer/TimerContext";
import {
  LABOR_QUESTIONNAIRE_DISPLAY_NAMES,
  QuestionnaireType,
} from "@/encounters/components/EncounterLabor/constants/labor-questionnaires";
import { LaborStageSelectField } from "@/encounters/components/EncounterLabor/EncounterLaborAmend/LaborStageSelectField";
import { BirthingFormFields } from "@/encounters/components/EncounterLabor/flowsheet-forms/birthing/components/BirthingFormFields";
import {
  BIRTHING_BODY_DATE_TIME_LINK_ID,
  BIRTHING_DATE_LINK_ID,
  BIRTHING_DATE_TIME_LINK_ID,
  BIRTHING_HEAD_DATE_TIME_LINK_ID,
  BIRTHING_QUESTIONNAIRE_NAME,
  BIRTHING_ROOT_LINK_ID,
  BIRTHING_TIME_LINK_ID,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/birthing/constants/birthing-link-ids";
import type { BirthingFlowsheetDrawerProps } from "@/encounters/components/EncounterLabor/flowsheet-forms/birthing/types/birthing-drawer";
import type { BirthingFlowsheetFormValues } from "@/encounters/components/EncounterLabor/flowsheet-forms/birthing/types/birthing-form";
import {
  getDefaultBirthingFlowsheetFormValues,
  normalizeBirthingFlowsheetFormValues,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/birthing/utils/birthing-form-defaults";
import {
  mapBirthingFormValuesToQuestionnaireValues,
  mapQuestionnaireResponseToBirthingFormValues,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/birthing/utils/birthing-questionnaire-mapping";
import { birthingFlowsheetFormSchema } from "@/encounters/components/EncounterLabor/flowsheet-forms/birthing/zod-schemas/birthing-flowsheet-form";
import {
  LABOR_FLOWSHEET_DRAWER_MODES,
  LABOR_FLOWSHEET_SUBMIT_BUTTON_TEXT,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/shared/labor-flowsheet-drawer-constants";
import { LaborFlowsheetDrawerShell } from "@/encounters/components/EncounterLabor/flowsheet-forms/shared/LaborFlowsheetDrawerShell";
import {
  type LaborFlowsheetDrawerEditContext,
  useLaborFlowsheetDrawerPersist,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/shared/useLaborFlowsheetDrawerPersist";
import { useBirthObservations } from "@/encounters/components/EncounterLabor/hooks/useBirthObservations";
import { completeAllEncounterTasks } from "@/encounters/components/EncounterLabor/services/labor";
import { useBirthMilestonesContext } from "@/encounters/contexts/BirthMilestonesProvider";

const BIRTHING_DISPLAY_NAME = LABOR_QUESTIONNAIRE_DISPLAY_NAMES[QuestionnaireType.Birthing];

function getBirthingEditContext(
  props: BirthingFlowsheetDrawerProps,
): LaborFlowsheetDrawerEditContext | undefined {
  if (props.mode !== LABOR_FLOWSHEET_DRAWER_MODES.Edit) {
    return undefined;
  }

  return {
    observation: props.observation,
    questionnaireDisplayName: props.questionnaireDisplayName,
  };
}

function mergeBirthingHydratedEditFormValues({
  mapped,
  hydratedQuestionnaireValues,
}: {
  mapped: BirthingFlowsheetFormValues;
  hydratedQuestionnaireValues: Record<string, unknown>;
}): BirthingFlowsheetFormValues {
  const hydratedRoot = hydratedQuestionnaireValues[BIRTHING_ROOT_LINK_ID] as
    | Record<string, unknown>
    | undefined;
  const hydratedDateTime = hydratedRoot?.[BIRTHING_DATE_TIME_LINK_ID] as
    | Record<string, unknown>
    | undefined;
  const hydratedBirthingDate = hydratedDateTime?.[BIRTHING_DATE_LINK_ID];
  const hydratedBirthingTime = hydratedDateTime?.[BIRTHING_TIME_LINK_ID];

  return {
    ...mapped,
    birthingDate: hydratedBirthingDate instanceof Date ? hydratedBirthingDate : mapped.birthingDate,
    birthingTime:
      typeof hydratedBirthingTime === "string" ? hydratedBirthingTime : mapped.birthingTime,
  };
}

export function BirthingFlowsheetDrawer(props: BirthingFlowsheetDrawerProps) {
  const { opened, onClose, clientId, encounterId, mode } = props;
  const isQuickAdd = mode === LABOR_FLOWSHEET_DRAWER_MODES.QuickAdd;
  const medplum = useMedplum();
  const { setupHeadDeliveredMilestone, setupBodyDeliveredMilestone } = useBirthMilestonesContext();
  const { stopTimer, hideAllTimerDisplays, deleteAllTimers } = useTimerContext();
  const { birthDateTime, setOptimisticBirth, clearOptimisticBirth } = useBirthObservations(
    encounterId,
    clientId,
  );

  const form = useForm<BirthingFlowsheetFormValues>({
    initialValues: getDefaultBirthingFlowsheetFormValues(),
    validate: zodResolver(birthingFlowsheetFormSchema),
  });

  const bodyDeliveredFormValue = form.values.birthingBodyDateTime;
  const lastSyncedBodyDeliveredRef = useRef<string | undefined>(undefined);
  const hasSubmittedRef = useRef(false);
  // Snapshot of the shared birth value from just before this drawer session's own first push, so
  // an unsaved close can restore it instead of always clearing to nothing - a value inherited
  // from elsewhere (e.g. Shoulder Dystocia, submitted while nested inside this still-open drawer)
  // must survive cancelling this draft, not just a value this draft created from scratch.
  const baselineBirthDateTimeRef = useRef<string | undefined>(undefined);
  const hasCapturedBaselineRef = useRef(false);
  // True once this open session has passed through a genuine loading cycle, so the sync/prefill
  // effects below don't act on a stale form value left over from a previous session during the
  // single render where `opened` has just flipped true but the persist hook's own reset hasn't
  // run yet (`isLoading` itself still reads its previous, pre-open value on that first render).
  const hasObservedLoadingRef = useRef(false);
  // Set right before the prefill effect mirrors an external birth-time change into the form, so
  // the sync effect below can tell "this push just reflects someone else's edit" apart from "the
  // user edited this field themselves" when deciding what baseline to restore on an unsaved close.
  const isMirroringExternalBirthTimeRef = useRef(false);
  // The last `birthDateTime` the prefill effect mirrored in, so it only reacts to a genuine change
  // in the shared value (including a clear) instead of re-running - and leaking the mirror flag
  // onto a later local edit - whenever some other dependency merely causes it to re-evaluate.
  const lastMirroredBirthDateTimeRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!opened) {
      hasCapturedBaselineRef.current = false;
      isMirroringExternalBirthTimeRef.current = false;
      lastMirroredBirthDateTimeRef.current = undefined;
    }
  }, [opened]);

  // Reverts an optimistic Body Delivered push if the drawer closes without the form having been
  // submitted, so an abandoned draft can't leave the shared birth state (and anything gated on it,
  // like the postpartum flowsheets) permanently showing a birth that was never actually recorded.
  // Only touches shared state if nothing else has changed it since this drawer's last sync -
  // otherwise a newer edit from elsewhere (e.g. Shoulder Dystocia, still open) would be clobbered.
  useEffect(() => {
    if (opened) {
      return;
    }

    const shouldRevertToBaseline =
      !hasSubmittedRef.current &&
      lastSyncedBodyDeliveredRef.current !== undefined &&
      birthDateTime === lastSyncedBodyDeliveredRef.current;

    if (shouldRevertToBaseline) {
      if (baselineBirthDateTimeRef.current) {
        setOptimisticBirth(baselineBirthDateTimeRef.current);
      } else {
        clearOptimisticBirth();
      }
    }

    lastSyncedBodyDeliveredRef.current = undefined;
    hasSubmittedRef.current = false;
    baselineBirthDateTimeRef.current = undefined;
  }, [opened, birthDateTime, setOptimisticBirth, clearOptimisticBirth]);

  const runQuickAddLocalSideEffects = useCallback(
    async ({ normalizedValues }: { normalizedValues: Record<string, unknown> }) => {
      hasSubmittedRef.current = true;

      const birthingRoot = normalizedValues[BIRTHING_ROOT_LINK_ID] as
        | Record<string, unknown>
        | undefined;
      const headDelivered = birthingRoot?.[BIRTHING_HEAD_DATE_TIME_LINK_ID];
      const bodyDelivered = birthingRoot?.[BIRTHING_BODY_DATE_TIME_LINK_ID];

      if (typeof headDelivered === "string" && headDelivered) {
        stopTimer("ROM");
        setupHeadDeliveredMilestone({ headDelivered });
      }

      if (typeof bodyDelivered === "string" && bodyDelivered) {
        stopTimer("Head Delivered");
        hideAllTimerDisplays();
        deleteAllTimers();
        setupBodyDeliveredMilestone({ bodyDelivered, updatedAtMs: Date.now() });
        await completeAllEncounterTasks(medplum, encounterId, clientId);
      }
    },
    [
      medplum,
      encounterId,
      clientId,
      stopTimer,
      hideAllTimerDisplays,
      deleteAllTimers,
      setupHeadDeliveredMilestone,
      setupBodyDeliveredMilestone,
    ],
  );

  const {
    isLoading,
    isSubmitting,
    loadError,
    dateTimeUtcDateAnchors,
    drawerLaborStageLoincCode,
    setDrawerLaborStageLoincCode,
    showLaborStageInDrawer,
    drawerTitle,
    handleSubmit,
  } = useLaborFlowsheetDrawerPersist<BirthingFlowsheetFormValues>({
    opened,
    mode,
    onClose,
    clientId,
    encounterId,
    questionnaireName: BIRTHING_QUESTIONNAIRE_NAME,
    displayName: BIRTHING_DISPLAY_NAME,
    timerKey: "birthing",
    form,
    parseAndNormalizeFormValues: (values) =>
      normalizeBirthingFlowsheetFormValues(birthingFlowsheetFormSchema.parse(values)),
    getDefaultFormValues: getDefaultBirthingFlowsheetFormValues,
    mapFormValuesToQuestionnaireValues: mapBirthingFormValuesToQuestionnaireValues,
    mapQuestionnaireResponseToFormValues: mapQuestionnaireResponseToBirthingFormValues,
    mergeHydratedEditFormValues: mergeBirthingHydratedEditFormValues,
    questionnaireNotFoundMessage: "Birthing questionnaire not found.",
    editEntryNotFoundMessage: "Could not load existing birthing entry.",
    editContext: getBirthingEditContext(props),
    onRefresh: props.mode === LABOR_FLOWSHEET_DRAWER_MODES.QuickAdd ? props.onRefresh : undefined,
    runQuickAddLocalSideEffects:
      mode === LABOR_FLOWSHEET_DRAWER_MODES.QuickAdd ? runQuickAddLocalSideEffects : undefined,
    title: props.title,
  });

  // Tracks whether this open session has actually seen a loading cycle, since `isLoading` itself
  // still reads its previous value on the single render where `opened` first flips true.
  useEffect(() => {
    if (!opened) {
      hasObservedLoadingRef.current = false;
      return;
    }
    if (isLoading) {
      hasObservedLoadingRef.current = true;
    }
  }, [opened, isLoading]);

  // Pushes Body Delivered edits into Redux as they happen, so other open forms (e.g. Shoulder
  // Dystocia) reflect the change immediately instead of waiting for this form to be submitted.
  // Gated on `hasObservedLoadingRef` (in addition to `!isLoading`) so a stale value left over from
  // a previous open can't get pushed on the render where `opened` flips true but the persist
  // hook's own quick-add reset-to-defaults hasn't run yet.
  useEffect(() => {
    if (!opened || !isQuickAdd || isLoading || !hasObservedLoadingRef.current) {
      return;
    }

    const wasMirroringExternalUpdate = isMirroringExternalBirthTimeRef.current;
    isMirroringExternalBirthTimeRef.current = false;

    const trimmedBody =
      bodyDeliveredFormValue && isValid(bodyDeliveredFormValue)
        ? bodyDeliveredFormValue.toISOString()
        : "";

    if (!trimmedBody) {
      if (lastSyncedBodyDeliveredRef.current !== undefined) {
        lastSyncedBodyDeliveredRef.current = undefined;
        clearOptimisticBirth();
      }
      return;
    }

    if (lastSyncedBodyDeliveredRef.current === trimmedBody) {
      return;
    }

    if (wasMirroringExternalUpdate) {
      // This push only reflects an external edit (e.g. Shoulder Dystocia) mirrored back in by the
      // prefill effect below, not a local draft - refresh the baseline so an unsaved close
      // restores this newer value instead of an earlier snapshot from before this session.
      baselineBirthDateTimeRef.current = trimmedBody;
      hasCapturedBaselineRef.current = true;
    } else if (!hasCapturedBaselineRef.current) {
      hasCapturedBaselineRef.current = true;
      baselineBirthDateTimeRef.current = birthDateTime;
    }

    lastSyncedBodyDeliveredRef.current = trimmedBody;
    setOptimisticBirth(trimmedBody);
  }, [
    opened,
    isQuickAdd,
    isLoading,
    bodyDeliveredFormValue,
    birthDateTime,
    setOptimisticBirth,
    clearOptimisticBirth,
  ]);

  // Mirrors the current birth time into Body Delivered (and whenever it changes while open,
  // including being cleared) so edits from other forms (e.g. Shoulder Dystocia) show up here too.
  // Gated on `hasObservedLoadingRef` (in addition to `!isLoading`) so this can't run before the
  // persist hook's own quick-add reset-to-defaults on the render where `opened` first flips true.
  useEffect(() => {
    if (!(opened && isQuickAdd && !isLoading && hasObservedLoadingRef.current)) {
      return;
    }

    if (lastMirroredBirthDateTimeRef.current === birthDateTime) {
      return;
    }
    lastMirroredBirthDateTimeRef.current = birthDateTime;

    isMirroringExternalBirthTimeRef.current = true;
    form.setFieldValue("birthingBodyDateTime", birthDateTime ? new Date(birthDateTime) : null);
    // `form` is stable from `useForm`; safe to omit from deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, isQuickAdd, isLoading, birthDateTime]);

  return (
    <LaborFlowsheetDrawerShell
      opened={opened}
      onClose={onClose}
      error={loadError}
      title={drawerTitle}
      isLoading={isLoading}
      onSubmit={handleSubmit}
      isSubmitting={isSubmitting}
      dateTimeUtcDateAnchors={dateTimeUtcDateAnchors}
      formId={`labor-flowsheet-form-${QuestionnaireType.Birthing}`}
      submitButtonText={
        mode === LABOR_FLOWSHEET_DRAWER_MODES.Edit
          ? LABOR_FLOWSHEET_SUBMIT_BUTTON_TEXT.Update
          : LABOR_FLOWSHEET_SUBMIT_BUTTON_TEXT.AddToFlowsheet
      }
      headerSection={
        showLaborStageInDrawer && drawerLaborStageLoincCode ? (
          <LaborStageSelectField
            value={drawerLaborStageLoincCode}
            onChange={setDrawerLaborStageLoincCode}
          />
        ) : null
      }
    >
      <BirthingFormFields form={form} mode={mode} clientId={clientId} isDrawerOpened={opened} />
    </LaborFlowsheetDrawerShell>
  );
}
